import { Link } from "react-router-dom";
import { useT } from "../i18n/I18nProvider";
import {
  ContentSection,
  AutoTOC,
  KeyPointsBlock,
  Sources,
  Warn,
  PL,
} from "../components/contentTemplate";

const SECTIONS = [
  { id: "sejour",     title: "Le droit au séjour" },
  { id: "fiscalite",  title: "L'imposition de votre pension" },
  { id: "sante",      title: "Votre couverture santé" },
  { id: "cout",       title: "Le coût de la vie" },
  { id: "securite",   title: "La sécurité au quotidien" },
  { id: "pratique",   title: "Les formalités pratiques" },
];

const INTERNAL = "text-[#0056D2] hover:underline";

const SOURCES = [
  { url: "https://bofip.impots.gouv.fr/bofip/958-PGP.html",
    label: "Convention fiscale France-Pologne du 20 juin 1975 (impots.gouv.fr / BOFiP)",
    description: "Date et texte de la convention, maintien en vigueur, distinction pensions privées (État de résidence) et publiques (État source)." },
  { url: "https://finances.belgium.be/fr/particuliers/declaration_impot/taux-revenus-imposables/habiter-recevoir-pension-differents-etats-membres-ue",
    label: "SPF Finances belge, pensions versées à un résident de l'étranger",
    description: "Règles particulières d'imposition, imposition belge possible et limitation conventionnelle selon le type de pension." },
  { url: "https://www.admin.ch/opc/fr/classified-compilation/19910210/index.html",
    label: "Administration fédérale suisse (admin.ch), convention Suisse-Pologne",
    description: "Convention du 2 septembre 1991 (RS 0.672.964.91), protocole en vigueur le 17 octobre 2011, notion de pension incluant les prestations en capital." },
  { url: "https://www.cleiss.fr/reglements/s1.html",
    label: "Cleiss et service-public.gouv.fr, document S1",
    description: "Rôle du S1, délivrance par la caisse de retraite, transfert de la prise en charge maladie, condition de non-perception d'une pension locale." },
  { url: "https://www.ameli.fr/assure/droits-demarches/europe-international/protection-sociale-etranger/retraite-etranger",
    label: "Ameli.fr, maintien des droits du pensionné",
    description: "Couverture selon les règles du pays de résidence, usage transitoire de la carte européenne." },
  { url: "https://udsc.gov.pl/en/cudzoziemcy/obywatele-ue-oraz-eogszwajcarii-i-czlonkowie-ich-rodzin/",
    label: "Office des étrangers polonais (Urząd do Spraw Cudzoziemców)",
    description: "Obligation d'enregistrement du séjour au-delà de trois mois pour les citoyens UE, EEE et Suisse, attestation gratuite et à durée indéterminée." },
  { url: "https://migrant.info.pl/fr/sejour-en-pologne-2590/",
    label: "Migrant.info.pl, conditions de séjour pour un citoyen inactif",
    description: "Ressources suffisantes et assurance maladie, séjour permanent après cinq ans." },
  { url: "https://fr.euronews.com/my-europe/2025/09/22/verification-des-faits-quel-est-le-pays-le-plus-sur-deurope",
    label: "Eurostat et Euronews, sécurité en Europe",
    description: "Classement de la Pologne parmi les pays les plus sûrs d'Europe selon l'expérience des résidents." },
  { url: "https://fr.numbeo.com/criminalit%C3%A9/",
    label: "Numbeo, indices comparés de criminalité et de sécurité",
    description: "Comparaisons par pays, source de l'indice cité en section 5." },
];

export default function RetraitePologne() {
  const { lp } = useT();

  return (
    <article
      className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16"
      data-testid="retraite-pologne-page"
    >
      <header className="mb-10" data-testid="page-hero">
        <span className="hej-chip" data-testid="page-chip">
          <span className="w-1.5 h-1.5 rounded-full bg-[#DC143C]"></span>
          Mis à jour en juin 2026
        </span>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mt-4">
          Prendre sa retraite en Pologne
        </h1>
      </header>

      <section className="space-y-4 text-slate-700 leading-relaxed text-lg mb-12" data-testid="page-lede">
        <p>
          La Pologne attire un nombre croissant de retraités francophones, et les raisons tiennent rarement au hasard : un coût de
          la vie sensiblement inférieur à celui de la France, de la Belgique ou de la Suisse, et une sécurité du quotidien parmi
          les meilleures d'Europe. Mais une installation réussie ne se joue pas sur ces arguments. Elle se joue sur trois questions
          concrètes que le contenu généraliste laisse souvent de côté : où sera imposée votre pension, comment vous serez soigné,
          et quelles démarches de séjour vous attendent.
        </p>
        <p>
          Ce guide s'adresse aux ressortissants de France, de Belgique et de Suisse. En matière de séjour comme de santé, ces trois
          nationalités bénéficient des règles simplifiées réservées aux citoyens de l'Union européenne et de la Suisse, très
          différentes de celles qui s'appliquent aux ressortissants de pays tiers. En matière fiscale, en revanche, chaque pays
          relève de sa propre convention avec la Pologne, et les différences comptent.
        </p>
      </section>

      <KeyPointsBlock testid="page-key-points">
        <ul className="list-disc pl-5 space-y-2">
          <li>Ressortissants français, belges et suisses : pas de <PL>Karta Pobytu</PL>. Un simple enregistrement de séjour auprès du voïvode suffit pour un séjour de plus de trois mois, sur preuve de ressources et d'assurance maladie.</li>
          <li>La pension du privé d'un résident fiscal polonais est imposée en Pologne, au barème local (12 % puis 32 %, avec une part de revenu non imposée de 30 000 zł). La pension publique reste en général imposée dans le pays d'origine.</li>
          <li>La convention applicable dépend de votre pays : France (1975), Belgique (2001, modifiée en 2018) et Suisse (1991, modifiée en 2011).</li>
          <li>La couverture santé se transfère grâce au document S1, qui ouvre l'accès au système public polonais (NFZ), souvent complété par une assurance privée.</li>
          <li>Le résident fiscal à l'étranger ne paie plus ni CSG ni CRDS françaises.</li>
        </ul>
      </KeyPointsBlock>

      <AutoTOC sections={SECTIONS} testid="page-toc" />

      <ContentSection id="sejour" n={1} title="Le droit au séjour">
        <p>
          Les ressortissants français, belges et suisses n'ont besoin ni de visa ni de <PL>Karta Pobytu</PL> pour vivre en Pologne.
          La <PL>Karta Pobytu</PL> concerne les ressortissants de pays tiers, et la confondre avec la procédure applicable aux
          citoyens de l'Union et de la Suisse est l'erreur la plus fréquente.
        </p>
        <p>
          Pour un séjour supérieur à trois mois, une seule obligation : enregistrer son séjour auprès du voïvode compétent (l'office
          régional, <PL>Urząd Wojewódzki</PL>). La demande se dépose en personne, au plus tard le lendemain de l'expiration de la
          période de trois mois suivant l'entrée en Pologne. Le document délivré est l'attestation d'enregistrement de séjour
          (<PL>zaświadczenie o zarejestrowaniu pobytu</PL>). La procédure est gratuite et l'attestation est valable pour une durée
          indéterminée.
        </p>
        <p>
          Pour un retraité, qui n'exerce pas d'activité professionnelle, deux conditions doivent être réunies : disposer de
          ressources suffisantes pour ne pas devenir une charge pour l'assistance sociale polonaise, ce que votre pension établit en
          règle générale, et disposer d'une assurance maladie appropriée, rôle que remplit précisément le document S1 décrit plus
          loin.
        </p>
        <p>
          Deux démarches préalables facilitent tout le reste. La déclaration d'adresse de résidence (<PL>zameldowanie</PL>) s'effectue
          à la mairie de quartier, et c'est l'occasion de demander un numéro PESEL, l'identifiant administratif qui ouvre l'accès aux
          services publics, bancaires et de santé. Ne confondez pas ces deux formalités : l'enregistrement du séjour se fait auprès
          du voïvode, alors que le <PL>zameldowanie</PL> est la déclaration d'adresse auprès de la commune. Nos{" "}
          <Link to={lp("/demarches")} className={INTERNAL}>démarches administratives</Link> détaillent l'obtention du PESEL et
          l'ouverture d'un compte bancaire. Après cinq ans de séjour ininterrompu, vous acquérez le droit au séjour permanent.
        </p>
      </ContentSection>

      <ContentSection id="fiscalite" n={2} title="L'imposition de votre pension">
        <p>
          Vous devenez résident fiscal polonais dès lors que vous résidez en Pologne plus de 183 jours par an, ou que vous y
          installez votre centre d'intérêts vitaux. À ce titre, vous déclarez vos revenus mondiaux en Pologne, sous réserve de la
          convention conclue entre la Pologne et votre pays d'origine.
        </p>
        <p>
          Le principe directeur est commun aux trois pays et tient en une distinction. Une pension du secteur privé versée à un
          résident de Pologne n'est imposable qu'en Pologne. Une pension publique, versée par l'État, une collectivité ou un
          organisme public, fait exception et reste en général imposable dans le pays qui la verse, souvent en lien avec la
          nationalité du bénéficiaire. Concrètement, deux retraités français voisins peuvent relever de deux pays d'imposition
          différents selon que l'un était salarié du privé et l'autre agent public.
        </p>
        <p>
          La pension imposée en Pologne entre dans le barème polonais de l'impôt sur le revenu : 12 % jusqu'à 120 000 zł de base
          imposable annuelle, puis 32 % au-delà. Le système prévoit une part de revenu non imposée de 30 000 zł. Le résident fiscal
          polonais ne paie ni CSG ni CRDS françaises.
        </p>
        <p>
          <strong>France.</strong> La convention applicable est celle du 20 juin 1975, toujours en vigueur. Votre pension du privé
          est imposée en Pologne, votre pension publique reste imposable en France.
        </p>
        <p>
          <strong>Belgique.</strong> La convention Belgique-Pologne prévoit des règles particulières pour les pensions. Certaines
          pensions privées ou assimilées peuvent être imposables en Pologne, mais les pensions belges versées au titre de la
          législation sociale ou d'un régime de retraite peuvent aussi rester imposables en Belgique, avec dans certains cas une
          limitation conventionnelle de l'impôt. Le traitement dépend donc du type exact de pension. Vérifiez votre situation
          auprès du SPF Finances ou d'un conseiller fiscal.
        </p>
        <p>
          <strong>Suisse.</strong> La convention du 2 septembre 1991, modifiée par un protocole entré en vigueur en 2011, régit la
          situation, et le cas suisse est le plus nuancé. La rente AVS du premier pilier versée à un résident de Pologne n'est pas
          imposée par la Suisse et relève de l'imposition polonaise. La rente et le capital du deuxième pilier (LPP) subissent en
          revanche un impôt suisse à la source au moment du versement, mais comme la convention attribue l'imposition à l'État de
          résidence, cet impôt à la source peut être réclamé en remboursement auprès de l'administration cantonale, sur présentation
          des justificatifs exigés par le canton, notamment le certificat de résidence fiscale polonais et, selon les cas, la
          preuve que la prestation a été déclarée ou portée à la connaissance de l'administration fiscale polonaise. La convention
          précise d'ailleurs que la notion de pension couvre aussi les prestations versées en capital, donc le retrait d'un
          deuxième ou d'un troisième pilier.
        </p>

        <div className="overflow-x-auto my-6" data-testid="fiscal-table-wrapper">
          <table className="w-full text-sm border border-slate-200 rounded-lg" data-testid="fiscal-table">
            <thead className="bg-slate-50 text-slate-700">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Pays</th>
                <th className="px-4 py-3 text-left font-semibold">Convention</th>
                <th className="px-4 py-3 text-left font-semibold">Pension privée</th>
                <th className="px-4 py-3 text-left font-semibold">Pension publique</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-slate-100">
                <td className="px-4 py-3 font-medium">France</td>
                <td className="px-4 py-3">20 juin 1975</td>
                <td className="px-4 py-3">Imposée en Pologne</td>
                <td className="px-4 py-3">Imposée en France</td>
              </tr>
              <tr className="border-t border-slate-100">
                <td className="px-4 py-3 font-medium">Belgique</td>
                <td className="px-4 py-3">20 août 2001 (mod. 2018)</td>
                <td className="px-4 py-3">À vérifier selon le type de pension ; imposition belge possible avec limitation conventionnelle</td>
                <td className="px-4 py-3">Imposée en Belgique</td>
              </tr>
              <tr className="border-t border-slate-100">
                <td className="px-4 py-3 font-medium">Suisse</td>
                <td className="px-4 py-3">2 sept. 1991 (mod. 2011)</td>
                <td className="px-4 py-3">Imposée en Pologne ; AVS non imposée en Suisse, impôt à la source du 2e pilier récupérable</td>
                <td className="px-4 py-3">Imposée en Suisse</td>
              </tr>
            </tbody>
          </table>
        </div>

        <Warn testid="fiscal-warn">
          <strong>Important.</strong> Ces règles posent les principes. Votre situation réelle peut dépendre de votre carrière, de
          votre nationalité, du type exact de pension et de l'articulation entre plusieurs régimes. Avant toute décision,
          rapprochez-vous de l'administration fiscale concernée et, le cas échéant, d'un conseiller fiscal. Ce guide n'a pas valeur
          de conseil fiscal personnalisé.
        </Warn>
      </ContentSection>

      <ContentSection id="sante" n={3} title="Votre couverture santé">
        <p>
          Le mécanisme central est le document S1. Il permet à un pensionné qui transfère sa résidence dans l'Union ou en Suisse de
          conserver sa couverture maladie, financée par son pays d'origine, tout en se faisant soigner sur place selon les règles
          locales.
        </p>
        <p>
          Pour un retraité français, le S1 se demande à la caisse de retraite qui verse la pension, et non à la caisse primaire
          d'assurance maladie. Pour un Belge ou un Suisse, il s'obtient auprès de l'institution compétente de son pays. Une fois en
          Pologne, vous remettez ce document au NFZ (<PL>Narodowy Fundusz Zdrowia</PL>, le fonds national de santé), qui vous inscrit.
          Votre pays d'origine continue de prendre en charge vos soins, dispensés selon la législation polonaise. Une condition à
          retenir : vous ne devez pas percevoir de pension polonaise vous ouvrant droit aux soins, sinon c'est la Pologne qui
          devient compétente.
        </p>
        <p>
          Un point pratique mérite d'être anticipé. La délivrance du S1 peut prendre plusieurs semaines, parfois davantage. Avant
          le départ, il est prudent de demander aussi une carte européenne d'assurance maladie. Elle peut couvrir des soins
          médicalement nécessaires lors de séjours temporaires, mais elle ne remplace pas le S1 pour une installation durable.
        </p>
        <p>
          Le S1 ouvre l'accès au système public polonais, dont la qualité et les délais restent en retrait par rapport à l'Europe
          de l'Ouest. C'est ce qui explique le recours fréquent à une assurance privée et aux réseaux de cliniques comme LuxMed ou
          Medicover, qui offrent des rendez-vous rapides et des praticiens souvent anglophones. Notre rubrique{" "}
          <Link to={lp("/sante")} className={INTERNAL}>Santé</Link> détaille cette articulation entre public et privé.
        </p>
      </ContentSection>

      <ContentSection id="cout" n={4} title="Le coût de la vie">
        <p>
          C'est l'argument le plus souvent avancé, et il est réel : à pension égale, votre pouvoir d'achat est nettement supérieur
          en Pologne. Le logement, l'alimentation et les services pèsent beaucoup moins lourd dans un budget mensuel qu'en France,
          en Belgique ou en Suisse. Plutôt que des moyennes nationales qui masquent de fortes différences entre Varsovie et une
          ville moyenne, nos rubriques{" "}
          <Link to={lp("/cout-de-la-vie")} className={INTERNAL}>Coût de la vie</Link> et{" "}
          <Link to={lp("/logement")} className={INTERNAL}>Logement</Link> donnent des chiffres par ville, mis à jour régulièrement,
          qui vous permettent de simuler un budget réaliste selon l'endroit où vous envisagez de vous installer. Si vous préférez
          acheter plutôt que louer, notre guide pour{" "}
          <Link to={lp("/acheter-bien-immobilier-pologne")} className={INTERNAL}>acheter un bien immobilier en Pologne</Link>{" "}
          précise les règles qui s'appliquent aux ressortissants européens et suisses.
        </p>
      </ContentSection>

      <ContentSection id="securite" n={5} title="La sécurité au quotidien">
        <p>
          La Pologne figure parmi les pays les plus sûrs d'Europe pour la criminalité du quotidien. Les données d'Eurostat, fondées
          sur l'expérience déclarée des résidents, la classent en tête, et le Global Peace Index situe sa criminalité violente
          parmi les plus basses du continent. À titre de comparaison, l'indice de criminalité de Numbeo pour 2026 plaçait la France
          à 55,9 sur 100, quand la Pologne se situait à 28,7, un niveau considéré comme faible.
        </p>
        <p>
          Une nuance s'impose, et elle renforce la crédibilité plutôt qu'elle ne l'affaiblit. La sécurité du quotidien ne doit pas
          être confondue avec le contexte géopolitique régional. Les régions frontalières de l'Ukraine et de la Biélorussie
          justifient une vigilance particulière, mais leur situation n'a qu'un effet limité sur le reste du territoire, où la vie
          suit son cours normalement.
        </p>
      </ContentSection>

      <ContentSection id="pratique" n={6} title="Les formalités pratiques">
        <p>
          Quelques démarches reviennent dans tous les dossiers. Signalez votre changement d'adresse à votre caisse de retraite :
          votre pension continue d'être versée à l'étranger, généralement par virement, et vous devrez fournir périodiquement un
          certificat de vie attestant que vous êtes toujours en vie. Ouvrez un compte bancaire polonais, utile pour les
          prélèvements locaux. Activez le <PL>Profil Zaufany</PL>, l'identité numérique qui simplifie la plupart des démarches en
          ligne avec les administrations polonaises. Prévoyez enfin des traductions assermentées de vos actes d'état civil,
          fréquemment demandées. Si vous comptez conserver une activité à la retraite, notre rubrique{" "}
          <Link to={lp("/entrepreneuriat")} className={INTERNAL}>Entrepreneuriat</Link> détaille les statuts et les cotisations ZUS
          applicables.
        </p>
        <p>
          Et si vous comptez travailler encore quelques années en Pologne avant la retraite, notre page{" "}
          <Link to={lp("/travailler-pologne")} className={INTERNAL}>Travailler en Pologne</Link>{" "}
          couvre les contrats, les salaires et les cotisations qui alimenteront vos droits.
        </p>
      </ContentSection>

      <Sources sources={SOURCES} testid="page-sources" />

      <Warn testid="page-disclaimer">
        Les informations de ce guide sont fournies à titre indicatif et ne constituent pas un conseil fiscal, juridique ou médical
        personnalisé. Les règles et les montants évoluent. Vérifiez votre situation auprès des administrations compétentes et, si
        nécessaire, d'un professionnel.
      </Warn>
    </article>
  );
}
